import type { EventScoreWithId } from "@moment-golf/types";

/**
 * Round-level fields that become locked once any player in that round has started.
 * These map to field names used in RoundDataLayout / RoundItemDisplay.
 */
export const ROUND_LOCKED_FIELDS: readonly string[] = [
    'courseName',
    'totalHoles',
    'startHole',
    'scoreFormat',
];

/**
 * Handicap usage fields that become locked per round once any player has started.
 * These are rendered by HandicapDisplay / HdcpItemDisplay.
 */
export const HDCP_LOCKED_FIELDS: readonly string[] = [
    'type',
    'indReduction',
    'compositeType',
    'compositeBuild',
];

/**
 * Event-level fields locked once any player in any round has started.
 * These map to field names used in EventDataLayout / EventItemDisplay.
 */
export const EVENT_LOCKED_FIELDS: readonly string[] = [
    'scoreType',
];

/**
 * Registration-level fields locked per player once that player has started any round.
 * These map to column fields in the RegistrationTab DataGrid.
 */
export const ALWAYS_LOCKED_FIELDS: readonly string[] = [
    'scoreCode',
    'playerHandicap',
    'teeSet',
    'gender',
];

const roundScoreStarted = (roundScore: any): boolean => {
    if (!roundScore) return false;
    if (roundScore.submitted === true) return true;
    if (roundScore.confirmedAt !== null && roundScore.confirmedAt !== undefined) return true;
    return roundScore.holeScores?.some((h: any) => (h.playerScore ?? 0) > 0) ?? false;
};

/** Check if a specific player's specific round has started */
export const hasPlayerRoundStarted = (
    eventScores: EventScoreWithId[],
    playerScoreCode: string,
    roundNumber: number,
): boolean => {
    const score = eventScores.find((s) => s.playerScoreCode === playerScoreCode);
    if (!score) return false;
    const round = score.roundScores?.find((r) => r.roundNumber === roundNumber);
    return roundScoreStarted(round);
};

/** Check if any player in a specific round has started (for round-level event field locking) */
export const hasAnyPlayerStartedRound = (
    eventScores: EventScoreWithId[],
    roundNumber: number,
): boolean => {
    return eventScores.some((score) => {
        const round = score.roundScores?.find((r) => r.roundNumber === roundNumber);
        return roundScoreStarted(round);
    });
};

/** Check if a specific player has started any round (for always-locked registration fields) */
export const hasPlayerStartedAnyRound = (
    eventScores: EventScoreWithId[],
    playerScoreCode: string,
): boolean => {
    const score = eventScores.find((s) => s.playerScoreCode === playerScoreCode);
    if (!score) return false;
    return (score.roundScores ?? []).some(roundScoreStarted);
};

/** Check if any player has started any round (for event-level field locking and button label) */
export const hasAnyPlayerStartedAnyRound = (
    eventScores: EventScoreWithId[],
): boolean => {
    return eventScores.some((score) =>
        (score.roundScores ?? []).some(roundScoreStarted)
    );
};
